import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  cloneSchema,
  deleteSchema,
  deprecateSchema,
  fetchSchemas,
  publishSchema,
} from '@/features/property-schemas/schemasThunks';
import { setActiveStatus } from '@/features/property-schemas/schemasSlice';
import { formatUnixDateTimeCompact } from '@/lib';
import { toast } from 'sonner';
import type { SchemaRecord, SchemaStatus } from '@/domain/entities/property-schema';

const STATUSES: SchemaStatus[] = ['draft', 'published', 'deprecated'];

const SchemasPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmDialog();

  // ✅ status-based state
  const { activeStatus, itemsByStatus, isListingByStatus, errorByStatus, hasFetchedByStatus, isDeleting } =
    useAppSelector((s) => s.propertySchemas);

  const items = itemsByStatus[activeStatus];
  const isListing = isListingByStatus[activeStatus];
  const error = errorByStatus[activeStatus];
  const hasFetched = hasFetchedByStatus[activeStatus];

  // ✅ fetch only when active tab hasn't been fetched yet (empty [] is still fetched)
  useEffect(() => {
    if (!hasFetched && !isListing) {
      dispatch(fetchSchemas({ status: activeStatus })); // <-- assumes thunk accepts {status}
    }
  }, [dispatch, activeStatus, hasFetched, isListing]);

  const navToAddSchema = (
    id?: string,
    options?: {
      version?: string; // e.g. "VER#3"
      readOnly?: boolean;
    },
  ) => {
    if (!id) {
      navigate('/admin/schemas/add');
      return;
    }

    // Read-only snapshot view (published / deprecated)
    if (options?.version) {
      navigate(`/admin/schemas/${encodeURIComponent(id)}/view/${encodeURIComponent(options.version)}`, {
        state: { readOnly: true },
      });
      return;
    }

    // Editable HEAD (draft only)
    navigate(`/admin/schemas/${encodeURIComponent(id)}/edit`, {
      state: { readOnly: options?.readOnly ?? false },
    });
  };

  const onChangeStatus = (status: SchemaStatus) => {
    dispatch(setActiveStatus(status));
  };

  const handleDelete = async (schemaId: string) => {
    if (activeStatus !== 'draft') return toast.error('Only draft schemas can be deleted.');
    if (isDeleting) return; // prevent multiple deletions

    const ok = await confirm({
      title: 'Delete this draft schema?',
      description: 'This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;

    const promise = dispatch(deleteSchema({ id: schemaId })).unwrap();

    toast.promise(promise, {
      loading: 'Deleting schema…',
      success: 'Schema deleted',
      error: (err) => err?.message || 'Failed to delete schema',
    });

    promise
      .then(async () => {
        await dispatch(fetchSchemas({ status: activeStatus }));
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handlePublish = async (schemaId: string) => {
    if (activeStatus !== 'draft') return;

    const ok = await confirm({
      title: 'Publish this schema?',
      description: 'This will create an immutable version (VER#n).',
      confirmLabel: 'Publish',
    });
    if (!ok) return;

    const promise = dispatch(publishSchema({ id: schemaId })).unwrap();

    toast.promise(promise, {
      loading: 'Publishing schema…',
      success: 'Schema published',
      error: (err) => err?.message || 'Failed to publish schema',
    });

    promise
      .then(async () => {
        // refresh both tabs (published gets the new VER#n)
        await Promise.all([
          dispatch(fetchSchemas({ status: 'draft' })),
          dispatch(fetchSchemas({ status: 'published' })),
        ]);
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handleClone = async (schemaId: string, version: string) => {
    const ok = await confirm({
      title: `Clone schema ${version}?`,
      description: 'This will create a new draft (HEAD).',
      confirmLabel: 'Clone',
    });
    if (!ok) return;

    const promise = dispatch(cloneSchema({ id: schemaId, version })).unwrap();

    toast.promise(promise, {
      loading: 'Cloning schema…',
      success: 'Schema cloned',
      error: (err) => err?.message || 'Failed to clone schema',
    });

    promise
      .then((res) => {
        const rec = res.data as SchemaRecord; // new HEAD draft returned by backend
        navigate(`/admin/schemas/${encodeURIComponent(rec.id)}/edit`, { replace: true });
      })
      .catch(() => {
        // toast already handled
      });
  };

  const handleDeprecate = async (schemaId: string, version: string) => {
    const ok = await confirm({
      title: 'Deprecate this schema?',
      description: 'This will prevent future use but keep it for historical records.',
      confirmLabel: 'Deprecate',
      variant: 'destructive',
    });
    if (!ok) return;

    const promise = dispatch(deprecateSchema({ id: schemaId, version })).unwrap();

    toast.promise(promise, {
      loading: 'Deprecating schema…',
      success: 'Schema deprecated',
      error: (err) => err?.message || 'Failed to deprecate schema',
    });

    promise.then(async () => {
      await Promise.all([
        dispatch(fetchSchemas({ status: 'published' })),
        dispatch(fetchSchemas({ status: 'deprecated' })),
      ]);
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <PageHeader title="My Schemas" />
        <Button onClick={() => navToAddSchema()}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Schema
        </Button>
      </div>

      {/* ✅ Segmented status switch */}
      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            type="button"
            variant={activeStatus === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChangeStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {error ? <div className="text-destructive rounded-lg border p-4 text-sm">{error}</div> : null}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">SCHEMA NAME</TableHead>
              <TableHead className="font-semibold">STATUS</TableHead>
              <TableHead className="font-semibold">LAST MODIFIED</TableHead>
              <TableHead className="font-semibold">VERSION</TableHead>
              <TableHead className="font-semibold">SCHEMA VERSION</TableHead>
              <TableHead className="text-right font-semibold">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isListing && !hasFetched ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  Loading schemas…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No schemas found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((schema) => (
                <TableRow key={`${schema.id}::${schema.version}`}>
                  <TableCell className="font-medium">{schema.title}</TableCell>
                  <TableCell className="text-muted-foreground">{schema.status}</TableCell>
                  <TableCell className="text-muted-foreground">{formatUnixDateTimeCompact(schema.updatedAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{schema.version}</TableCell>
                  <TableCell className="text-muted-foreground">{schema.schemaVersion}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-4">
                      {/* Edit (HEAD) or View (VER#n) */}
                      <Button
                        variant="link"
                        className="h-auto p-0"
                        onClick={
                          () =>
                            schema.version !== 'HEAD'
                              ? navToAddSchema(schema.id, { version: schema.version }) // read-only snapshot
                              : navToAddSchema(schema.id) // editable HEAD
                        }
                      >
                        {schema.version !== 'HEAD' ? 'View' : 'Edit'}
                      </Button>

                      {/* Draft-only publish */}
                      {activeStatus === 'draft' ? (
                        <Button variant="link" className="h-auto p-0" onClick={() => handlePublish(schema.id)}>
                          Publish
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="link"
                            className="h-auto p-0"
                            onClick={() => handleClone(schema.id, schema.version)}
                          >
                            Clone
                          </Button>

                          {activeStatus !== 'deprecated' ? (
                            <Button
                              variant="link"
                              className="h-auto p-0"
                              onClick={() => handleDeprecate(schema.id, schema.version)}
                            >
                              Deprecate
                            </Button>
                          ) : null}
                        </>
                      )}

                      {/* Draft-only delete */}
                      {activeStatus === 'draft' ? (
                        <Button
                          variant="link"
                          className="text-destructive hover:text-destructive h-auto p-0"
                          onClick={() => handleDelete(schema.id)}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SchemasPage;
